import { action, actionOn, thunk } from "easy-peasy";
import axios from '../services/axios';

const activitiesInitialState = {
    propsFetched: {},
    allActivity: null,
    isFetchingAllActivity: false,
    isUpdateNecessary: false,
    initialFetchDone: false,
    activityById: null,
    isFetchingOneActivity: false,
    webapp2Counts: null,
    isGradualFetchPropsNecessary: true,
}

const activities = {
    ...activitiesInitialState,

    onGlobalResetCalled: actionOn(
        (actions, storeActions) => [
            storeActions.resetStore.performReset,
        ],
        (state, target) => {
            const [performReset] = target.resolvedTargets;

            if (target.type === performReset) {
                ({
                    propsFetched: state.propsFetched,
                    allActivity: state.allActivity,
                    isFetchingAllActivity: state.isFetchingAllActivity,
                    isUpdateNecessary: state.isUpdateNecessary,
                    initialFetchDone: state.initialFetchDone,
                    activityById: state.activityById,
                    isFetchingOneActivity: state.isFetchingOneActivity,
                    webapp2Counts: state.webapp2Counts,
                    isGradualFetchPropsNecessary: state.isGradualFetchPropsNecessary,
                } = activitiesInitialState);
            }
        }
    ),

    setInitialFetchDone: action((state, payload) => {
        state.initialFetchDone = payload;
    }),

    setIsUpdateNecessary: action((state, payload) => {
        state.isUpdateNecessary = payload;
    }),

    setAllActivity: action((state, payload) => {
        state.allActivity = payload;
    }),

    setIsFetchingAllActivity: action((state, payload) => {
        state.isFetchingAllActivity = payload;
    }),

    setActivityById: action((state, payload) => {
        state.activityById = payload;
    }),
    setIsFetchingOneActivity: action((state, payload) => {
        state.isFetchingOneActivity = payload;
    }),

    addActivity: action((state, payload) => {
        state.allActivity.push(payload);
    }),

    setWebapp2Counts: action((state, payload) => {
        state.webapp2Counts = payload;
    }),

    setIsGradualFetchPropsNecessary: action((state, payload) => {
        state.isGradualFetchPropsNecessary = payload;
    }),

    setPropsFetched: action((state, payload) => {
        state.propsFetched[payload] = true;
    }),

    updateAllActivity: action((state, payload) => {

        if (state.allActivity?.length > 0) {
            const index = state.allActivity?.findIndex((activity) => activity.uniqueId === payload.uniqueId);

            if (index !== -1) {
                state.allActivity[index] = {
                    ...state.allActivity[index],
                    ...payload,
                };
            }
        }

    }),

    removeFromAllActivity: action((state, payload) => {
        const indexToRemove = state.allActivity?.findIndex(activity => activity.uniqueId === payload);
        (indexToRemove !== -1) && state.allActivity.splice(indexToRemove, 1);
    }),

    fetchAllActivity: thunk((actions, payload, helpers) => {
        const { isFetchingAllActivity, isUpdateNecessary } = helpers.getState();
        const { apiData: { ACTIONS } } = helpers.getStoreState().actionSlugs;

        const getUrl = ACTIONS?.activity?.readAll?.url;

        if (!isFetchingAllActivity && isUpdateNecessary) {
            actions.setIsFetchingAllActivity(true);

            return axios.get(getUrl)
                .then(data => {
                    actions.setAllActivity(data);
                    actions.setIsUpdateNecessary(activitiesInitialState.isUpdateNecessary);
                    return data;
                })
                .catch(error => {
                    return Promise.reject(error);
                })
                .finally(() => {
                    actions.setIsFetchingAllActivity(activitiesInitialState.isFetchingAllActivity);
                })
        } else {
            return Promise.resolve();
        }
    }),

    createActivity: thunk((actions, payload, helpers) => {
        const { apiData: { ACTIONS } } = helpers.getStoreState().actionSlugs;

        const createActivityUrl = ACTIONS?.activity?.create?.url;
        const csrfToken = ACTIONS?.activity?.create?.csrfToken;
        const { formData } = payload;
        formData.append('token', csrfToken);

        return axios.post(createActivityUrl, formData)
            .then((result) => {
                actions.addActivity(result);
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            });
    }),

    deleteActivity: thunk((actions, payload, helpers) => {
        const { uniqueId } = payload;
        const { apiData: { ACTIONS } } = helpers.getStoreState().actionSlugs;
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const deleteActivityUrl = apiData.ACTIONS?.activity?.delete?.url?.replace('uniqueId', uniqueId);
        payload.token = ACTIONS?.activity?.delete?.csrfToken;

        return axios.post(deleteActivityUrl, payload)
            .then((deletedActivity) => {
                actions.removeFromAllActivity(deletedActivity.uniqueId);
                return Promise.resolve();
            })
            .catch(error => {
                return Promise.reject(error);
            });
    }),

    updateActivity: thunk((actions, payload, helpers) => {
        const { uniqueId, formData } = payload;
        const { apiData: { ACTIONS } } = helpers.getStoreState().actionSlugs;
        const updateActivityUrl = ACTIONS?.activity?.update?.url?.replace('uniqueId', uniqueId);
        const csrfToken = ACTIONS?.activity?.update?.csrfToken;

        formData.append('token', csrfToken);

        return axios.post(updateActivityUrl, formData)
            .then((result) => {
                actions.updateAllActivity(result);
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            });
    }),

    fetchActivityById: thunk((actions, payload, helpers) => {
        const { isFetchingOneActivity } = helpers.getState();
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const getOneUrl = apiData.ACTIONS?.activity?.readOne?.url?.replace('uniqueId', payload);

        if (!isFetchingOneActivity) {
            actions.setIsFetchingOneActivity(true);
            actions.setActivityById(null);

            return axios.get(getOneUrl)
                .then((data) => {
                    actions.setIsFetchingOneActivity(activitiesInitialState.isFetchingOneActivity);
                    actions.setActivityById(data);
                })
                .catch(error => Promise.reject(error));
        } else {
            return Promise.resolve();
        }
    }),

    editActivityStatus: thunk((actions, payload, helpers) => {
        const { apiData: { ACTIONS } } = helpers.getStoreState().actionSlugs;
        const { uniqueId } = payload;
        const postUrl = ACTIONS?.activity?.editValidated?.url.replace('uniqueId', uniqueId);
        payload.token = ACTIONS?.activity?.editValidated?.csrfToken;

        return axios.post(postUrl, payload)
            .then(data => {
                actions.updateAllActivity(data);
                actions.setActivityById(data);
                return data;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),


    fetchWebapp2Counts: thunk((actions, payload, helpers) => {

        const { webapp2Counts } = helpers.getState();
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const getCountsUrl = apiData.ACTIONS?.webapp2User?.getAllProps?.getWebapp2Counts?.url;

        if (!webapp2Counts && getCountsUrl) {

            return axios.get(getCountsUrl)
                .then((data) => {
                    actions.setWebapp2Counts(data);
                })
                .catch(error => Promise.reject(error));
        } else {
            return Promise.resolve();
        }
    }),

    updateActivityProps: thunk((actions, payload, helpers) => {
        const allActivity = helpers.getState().allActivity;

        const newAllActivity = allActivity.map(oldActivity => {
            const newActivity = payload.find(act => act.uniqueId === oldActivity.uniqueId);
            return { ...oldActivity, ...newActivity }
        });
        actions.setAllActivity(newAllActivity);
    }),

    fetchActivityProps: thunk((actions, payload) => {
        const prop = payload;

        if (prop?.url) {
            return axios.get(prop?.url)
                .then(data => {
                    actions.updateActivityProps(data);
                    actions.setPropsFetched(prop?.name);
                    return data;
                })
                .catch((error) => {
                    return Promise.reject(error);
                })
        }
    }),

    gradualFetchActivitiesProps: thunk((actions, payload, helpers) => {
        const { apiData } = helpers.getStoreState().actionSlugs;
        const { propsFetched, isGradualFetchPropsNecessary } = helpers.getState();
        if (isGradualFetchPropsNecessary && apiData.ACTIONS.activity) {
            const propsForAll = apiData.ACTIONS.activity.getPropsForAll;
            const gradualAccessRoutes = propsForAll
                ? Object.keys(propsForAll).map(keyProp => {
                    return {
                        name: keyProp,
                        url: propsForAll[keyProp].url,
                    };
                })
                : [];

            if (gradualAccessRoutes.length > 0) {

                let gradualFetchToBeDone = [];
                gradualAccessRoutes.forEach(prop => {
                    if (propsFetched[prop?.name] === undefined) {
                        gradualFetchToBeDone.push(actions.fetchActivityProps(prop));
                    }
                });
                return Promise.all(gradualFetchToBeDone).then(function (results) {
                    actions.setIsGradualFetchPropsNecessary(false);
                })
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    }),

};

export default activities;